import useAiChatRepo from '@/composables/ai-chat/ai-chat-repo';
import useSnackBar from '@/composables/snackbar';
import { chatTitle, groupChatsByRecency } from '@/lib/ai-chat-list';
import { computed, ref } from 'vue';
import { logCatch } from '@/lib/logger/logger';
import { useI18n } from 'vue-i18n';

const ITEMS_PER_PAGE = 25;

// every list shows the same pinned group, and the query behind it is unpaginated
let pinnedChatsLoad = null;

export const resetPinnedChats = () => { pinnedChatsLoad = null; };

export default function useAiChatList({ searchTerm = null } = {}) {
  const { t } = useI18n();
  const snackbar = useSnackBar();
  const { chats, getListLoading, queryChatList, queryPinnedChats } = useAiChatRepo();

  const page = ref(0);
  const fetchingPage = ref(0);
  const exhausted = ref(false);
  const loadedTerm = ref(null);

  const term = computed(() => (searchTerm === null ? '' : searchTerm.value.trim()));

  const title = (chat) => chatTitle(chat, t('aiChat.untitled'));

  // the server filter decides which chats get fetched, but nebula keeps every chat ever
  // loaded, so the term has to narrow the store again before the list is rendered
  const matches = computed(() => {
    const needle = term.value.toLowerCase();
    if (needle === '') {
      return chats.value;
    }
    return chats.value.filter((chat) => title(chat).toLowerCase().includes(needle));
  });

  const groups = computed(() => groupChatsByRecency(matches.value));

  const searching = computed(() => getListLoading.value && fetchingPage.value === 1);
  const loadingMore = computed(() => getListLoading.value && fetchingPage.value > 1);
  const stale = computed(() => loadedTerm.value !== term.value);

  const fetchPage = (next) => {
    fetchingPage.value = next;
    return queryChatList({
      pagination: { page: next, itemsPerPage: ITEMS_PER_PAGE },
      searchTerm: term.value,
    }).then(({ count }) => {
      page.value = next;
      loadedTerm.value = term.value;
      exhausted.value = matches.value.length >= count;
    }).catch(logCatch(() => { snackbar.error(); }));
  };

  const restart = () => {
    exhausted.value = false;
    return fetchPage(1);
  };

  const loadMore = () => {
    if (getListLoading.value || exhausted.value) {
      return;
    }
    fetchPage(page.value + 1);
  };

  if (pinnedChatsLoad === null) {
    pinnedChatsLoad = queryPinnedChats().catch(logCatch(() => {
      pinnedChatsLoad = null;
      snackbar.error();
    }));
  }

  return {
    groups,
    title,
    loading: getListLoading,
    searching,
    loadingMore,
    stale,
    restart,
    loadMore,
  };
}
